import React, {Component} from 'react';
import { GET_PROPERTIES } from '../queries';
import { Button, Modal, Form } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo, WithApolloClient } from 'react-apollo';
import {createSocket} from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from "@fortawesome/free-solid-svg-icons";
// Save on bundle size / build time w/ smaller bundle
// https://github.com/plotly/react-plotly.js#customizing-the-plotlyjs-bundle
//import Plotly from 'plotly.js-gl2d-dist';
import createPlotlyComponent from 'react-plotly.js/factory';
import GeoJSON from 'ol/format/GeoJSON.js'; // eslint-disable-line
import { scales, getArrayMin, getArrayMax } from'../colorscaleUtils';
const Plot = createPlotlyComponent(window.Plotly);

const plotlyColorscaleNames = Object.keys(scales);

const UNSELECTED_OPACITY = 0.05;
const SELECTED_OPACITY = 1.0;

interface DashboardChartWidgetProps {
  [key: string]: any; // Allow any additional props
}

class DashboardChartWidget extends Component<WithApolloClient<DashboardChartWidgetProps>, any> {
    constructor(props: any) {
        super(props);

        this.state = {
            showModal: false,
            xPropId: null,
            yPropId: null,
            colorByPropId: null,
            xProp: null,
            yProp: null,
            colorByProp: null,
            colorscaleName: null,
            xData: [],
            yData: [],
            colorByData: [],
            opacityData: [],
            colorByMin: null,
            colorByMax: null,
            hoverText: [],
            annotations: [],
            chartType: null,
            // so changing annotations will force a re-render since identity of layout object doesn't change:
            revision: 0, 
            propertiesById: null,
            isLinkedToMap: false,
        }

        this.sessionId = props.sessionId as string;
        this.socket = createSocket(this.serverUrl, this.sessionId, true);

        this.chartPropertiesForm = this.chartPropertiesForm.bind(this);
        this.loadProperties = this.loadProperties.bind(this);
        this.annotateSelectedEntity = this.annotateSelectedEntity.bind(this);
        this.handleMapSelect = this.handleMapSelect.bind(this);
        this.handlePlotSelect = this.handlePlotSelect.bind(this);

    }

    private serverUrl = process.env.REACT_APP_CHATBOT_URL as string;
    private sessionId: string;
    private socket: SocketIOClient.Socket;

    private aoiBounds: number[]|null = null;
    private mapSelectBounds: number[]|null = null;
    private entities: any[]|null = null;

    componentDidMount() {
        this.socket.on('map', (msg: any) => {
            // console.log('chart got map msg', msg);

            if (msg.command !== 'fitBounds' || !msg.data) {
                return;
            }

            // TODO what does chatbot do if AOI is arbitrary polygon?
            // (can use edit voice command to make arbitrary edits to AOI polygon)
            const [miny, maxy, minx, maxx] = msg.data;
            this.aoiBounds = [miny, maxy, minx, maxx];

            if (this.state.xProp && this.state.yProp && this.state.colorByProp && this.state.propertiesById) {
                this.refreshChartData(this.state.xProp.id, this.state.yProp.id, this.state.colorByProp.id, this.state.chartType, this.state.colorscaleName);
            }
        });

        this.socket.on('chart', (msg: any) => {
            // console.log('chart got chart msg', msg);
            if (msg.command === 'selectEntity') {
                this.annotateSelectedEntity(msg.entityId);
            } else if (msg.command === 'loadChartData' && msg.widgetInstanceId) {
                if (msg.widgetInstanceId !== this.props.widgetInstance.id) {
                    // only listen to the same widget instance;
                    // allows multiple independent graphs per session
                    return;
                }

                const xPropId = msg.xPropId;
                const yPropId = msg.yPropId;
                const colorByPropId = msg.colorByPropId;
                const chartType = msg.chartType;
                const colorscaleName = msg.colorscaleName;
                if (xPropId && yPropId && colorByPropId && chartType && colorscaleName && this.aoiBounds !== null && this.state.propertiesById) {
                    // refreshChartData handles the setState
                    this.refreshChartData(xPropId, yPropId, colorByPropId, chartType, colorscaleName);
                } else if (xPropId && yPropId && colorByPropId && chartType && colorscaleName) {
                    this.setState((state: any) => {
                        // no guarantee whether this or property load will happen first,
                        // so save prop id if property objects aren't loaded yet
                        const newState: any = {
                            xPropId,
                            yPropId,
                            colorByPropId,
                            chartType,
                            colorscaleName,
                        };
                        if (this.state.propertiesById) {
                            newState.xProp = this.state.propertiesById[xPropId];
                            newState.yProp = this.state.propertiesById[yPropId];
                            newState.colorByProp = this.state.propertiesById[colorByPropId];
                        }
                        return newState;
                    });
                }
            } else if (msg.command === 'setMapChartLink' && msg.widgetInstanceId && this.props.widgetInstance) {
                // console.log('chartWidget setMapChartLink', msg.widgetInstanceId, this.props.widgetInstance.id);
                this.setState({
                    isLinkedToMap: msg.widgetInstanceId === this.props.widgetInstance.id,
                });
            } else if (msg.command === 'mapSelect') {
                const { minx, miny, maxx, maxy } = msg;
                // console.log('chart got mapSelect', minx, miny, maxx, maxy);
                this.mapSelectBounds = [minx, miny, maxx, maxy];
                this.handleMapSelect();
            } else if (msg.command === 'plotSelect') {
                this.handlePlotSelect(msg);
            }
        });

        this.loadProperties();
    }

    handlePlotSelect(msg: any) {
        if (msg.xPropId === null) {
            // this message indicates deselect
            this.setState((state: any) => {
                return {
                    opacityData: state.xData.map(() => SELECTED_OPACITY),
                    revision: state.revision + 1,
                };
            });
            return;
        }
        if (!this.entities) {
            return;
        }

        const { xStart, xEnd, yStart, yEnd, xPropId, yPropId } = msg;
        const opacityData: number[] = [];
        this.entities.forEach((entity: any) => {
            let xStat = null;
            let yStat = null;
            entity.stats.forEach((stat: any) => {
                if (parseInt(stat.property.id) === xPropId) {
                    if (stat.median !== null && stat.median !== undefined) {
                        xStat = stat.median;
                    } else if (stat.mean !== null && stat.mean !== undefined) {
                        xStat = stat.mean;
                    } 
                }
                if (parseInt(stat.property.id) === yPropId) {
                    if (stat.median !== null && stat.median !== undefined) {
                        yStat = stat.median;
                    } else if (stat.mean !== null && stat.mean !== undefined) {
                        yStat = stat.mean;
                    }
                }
            });
            if (xStat !== null && yStat !== null) {
                if (xStat > xStart && xStat < xEnd && yStat > yStart && yStat < yEnd) {
                    opacityData.push(SELECTED_OPACITY);
                } else {
                    opacityData.push(UNSELECTED_OPACITY);
                }
            } else {
                // If relevant property doesn't exist on this entity,
                // I guess it should be unselected?
                opacityData.push(UNSELECTED_OPACITY);
            }
        });
        this.setState((state: any) => {
            return {
                opacityData,
                revision: state.revision + 1,
            };
        });
    }

    handleMapSelect() {
        if (!this.entities || !this.mapSelectBounds) {
            return;
        }
        const [minx, miny, maxx, maxy] = this.mapSelectBounds;
        const opacityData: number[] = [];
        // var gj = new GeoJSON();
        this.entities.forEach((entity: any) => {
            /* works, but this loop needs to be as performant as possible
            const featureGeojson = {
                type: 'Feature',
                geometry: entity.point ? entity.point : entity.multipolygon,
            };
            const feature = gj.readFeature(featureGeojson);
            if (feature.getGeometry().intersectsExtent(this.mapSelectBounds)) {
                opacityData.push(SELECTED_OPACITY);
            } else {
                opacityData.push(UNSELECTED_OPACITY);
            }
            */

            // TODO profile this compared to above intersectsExtent method -
            //      doesn't seem like this way is actually all that much faster
            let x, y;
            if (entity.point) {
                [x, y] = entity.point.coordinates;
            } else if (entity.multipolygon) {
                if (entity.multipolygon.coordinates && entity.multipolygon.coordinates.length 
                    && entity.multipolygon.coordinates[0].length
                    && entity.multipolygon.coordinates[0][0].length)
                {
                    // simplify this by just picking first point of multipolygon
                    [x, y] = entity.multipolygon.coordinates[0][0][0];
                } else {
                    console.warn('multipolygon entity has no coordinates', entity);
                    opacityData.push(SELECTED_OPACITY);
                    return;
                }
            } else {
                console.warn('both point and multipolygon are null', entity);
                opacityData.push(SELECTED_OPACITY);
                return;
            }
            // console.log('x: ', x, 'y: ', y);
            if (x > minx && x < maxx && y > miny && y < maxy) {
                opacityData.push(SELECTED_OPACITY);
            } else {
                opacityData.push(UNSELECTED_OPACITY);
            }
        });
        this.setState((state: any) => {
            return {
                opacityData,
                revision: state.revision + 1,
            };
        });
    }

    annotateSelectedEntity(entityId: any) {
        // TODO make "entitiesById" object for more efficient lookup
        const entityIndex = this.state.hoverText.indexOf(entityId);
        if (entityIndex >= 0) {
            const x = this.state.xData[entityIndex];
            const y = this.state.yData[entityIndex];
            // console.log('setState new annotation', msg.entityId, x, y);
            this.setState((state: any) => {
                return {
                    annotations: [{
                        x,
                        y,
                        text: entityId as string,
                        arrowcolor: 'white',
                    }],
                    revision: state.revision + 1,
                };
            });
        }
    }

    loadProperties() {
        this.props.client.query({
            query: GET_PROPERTIES
        })
        .then((result: any) => {
            const properties = result.data.syncarto_dw_property;
            const propertiesById: any = {};
            properties.forEach((prop: any) => {
                propertiesById[prop.id] = prop;
            });
            this.setState({
                propertiesById,
            }, () => {
                // console.log('property list done loading');
                if (this.state.xProp && this.state.yProp && this.state.colorByProp && this.state.chartType 
                    && this.aoiBounds !== null 
                    && !this.state.xData.length && !this.state.yData.length && !this.state.colorByData.length)
                {
                    // on page refresh,
                    // whichever of aoi restore, property list query, or xprop/yprop restore
                    // finishes last needs to cause the chart data to load.
                    this.refreshChartData(this.state.xProp.id, this.state.yProp.id, this.state.colorByProp.id, this.state.chartType, this.state.colorscaleName);
                } else if (this.state.xPropId && this.state.yPropId && this.state.colorByPropId && this.state.chartType) {
                    // If this happens after loadChartData restore, we need to set the actual
                    // xProp and yProp state objects based on the id's from loadChartData restore.
                    this.refreshChartData(this.state.xPropId, this.state.yPropId, this.state.colorByPropId, this.state.chartType, this.state.colorscaleName);
                }
            });
        })
        .catch((error: any) => console.error(error));
    }

    refreshChartData(xPropId: number, yPropId: number, colorByPropId: number, chartType: string, colorscaleName?: string) {
        if (this.aoiBounds === null) {
            return;
        }

        this.props.setWidgetTitleStatus('loading data');

        const [miny, maxy, minx, maxx] = this.aoiBounds;

        this.props.client.query({
          query: gql`
            query syncarto_dw_entity_by_polygon($extent: geometry) {
              syncarto_dw_entity(where: {_or: [{point: {_st_within: $extent}}, {multipolygon: {_st_intersects: $extent}}]}) {
                entity_source_id
                entity_type_id
                ext_id
                geo_type_id
                multipolygon
                point
                id
                is_public
                name
                org_id
                # stats(where: {_or: [{property_id: {_eq: ${xPropId}}}, {property_id: {_eq: ${yPropId}}}, {property_id: {_eq: ${colorByPropId}}}]}) {
                # we actually need every property in order to support graph-to-graph cross filtering
                stats {
                  median
                  mean
                  property {
                    id
                  }
                }
              }
            }
          `,
          variables: {
                extent: {
                    "crs": {
                        "type": "name",
                        "properties": {
                            "name": "urn:ogc:def:crs:EPSG::4326"
                        }
                    },
                    "type": "Polygon",
                    "coordinates": 
                    [ [ [ minx, miny ], 
                        [ minx, maxy ],
                        [ maxx, maxy ], 
                        [ maxx, miny ],
                        [ minx, miny ] ] ] 

                }
            }
        })
        .then((result: any) => {
            const allEntities = result.data.syncarto_dw_entity;
            const displayedEntities: any[] = []
            // console.log('FetchChartData', data);
            const xData: number[] = [];
            const yData: number[] = [];
            const colorByData: number[] = [];
            const opacityData: number[] = [];
            const hoverText: string[] = [];
            allEntities.forEach((entity: any) => {
                let xStat = null;
                let yStat = null;
                let colorByStat = null;
                entity.stats.forEach((stat: any) => {
                    if (parseInt(stat.property.id) === xPropId) {
                        if (stat.median !== null && stat.median !== undefined) {
                            xStat = stat.median;
                        } else if (stat.mean !== null && stat.mean !== undefined) {
                            xStat = stat.mean;
                        } 
                    }
                    if (parseInt(stat.property.id) === yPropId) {
                        if (stat.median !== null && stat.median !== undefined) {
                            yStat = stat.median;
                        } else if (stat.mean !== null && stat.mean !== undefined) {
                            yStat = stat.mean;
                        }
                    }
                    if (parseInt(stat.property.id) === colorByPropId) {
                        if (stat.median !== null && stat.median !== undefined) {
                            colorByStat = stat.median;
                        } else if (stat.mean !== null && stat.mean !== undefined) {
                            colorByStat = stat.mean;
                        }
                    }
                })
                // both properties must be non null or arrays don't make sense together
                if (xStat !== null && yStat !== null && colorByStat !== null) {
                    hoverText.push(entity.id as string);
                    xData.push(xStat);
                    yData.push(yStat);
                    colorByData.push(colorByStat);
                    opacityData.push(SELECTED_OPACITY);
                    displayedEntities.push(entity);
                }
            });
            const colorByMin = getArrayMin(colorByData);
            const colorByMax = getArrayMax(colorByData);

            this.entities = displayedEntities;
            this.setState((state: any) => {
                return {
                    xData,
                    yData,
                    colorByData,
                    opacityData,
                    colorByMin,
                    colorByMax,
                    hoverText,
                    revision: state.revision + 1,
                    chartType,
                    xProp: state.propertiesById[xPropId],
                    yProp: state.propertiesById[yPropId],
                    colorByProp: state.propertiesById[colorByPropId],
                    xPropId,
                    yPropId,
                    colorByPropId,
                    colorscaleName: colorscaleName ? colorscaleName : undefined,
                };
            });
            this.socket.emit('chart', {
                command: 'setColorscale',
                colorscaleName,
                colorByMin,
                colorByMax,
                colorByPropName: this.state.propertiesById[colorByPropId].name,
                xPropId,
                yPropId,
                colorByPropId,
                chartType,
                widgetInstanceId: this.props.widgetInstance.id,
                widgetInstanceTitle: this.props.widgetInstance.title,
            });
            this.props.setWidgetTitleStatus('');
        })
        .catch((error: any) => console.error(error));
    }
    
    chartPropertiesForm = () => {
        if (!this.state.propertiesById) {
            return (<div>Loading...</div>);
        }

        const propertyIds = Object.keys(this.state.propertiesById);

        return (
            <Form onSubmit={(e: any) => this.handleSubmit(e)}>
              <Form.Group controlId="ChartTypeControlSelect">
                <Form.Label>type</Form.Label>
                <Form.Control as="select" name="chartType"
                  defaultValue={this.state.chartType? this.state.chartType : undefined}>
                  <option>scattergl</option>
                  {/*<option>table</option> */}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="XPropControlSelect">
                <Form.Label>X</Form.Label>
                <Form.Control as="select" name="xPropId" 
                  defaultValue={this.state.xProp? this.state.xProp.id : undefined}>
                    {propertyIds.map((id: any, i: number) => (
                      <option key={i} value={id}>{this.state.propertiesById[id].name}</option>
                    ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="YPropControlSelect">
                <Form.Label>Y</Form.Label>
                <Form.Control as="select" name="yPropId" 
                  defaultValue={this.state.yProp? this.state.yProp.id : undefined}>
                    {propertyIds.map((id: any, i: number) => (
                      <option key={i} value={id}>{this.state.propertiesById[id].name}</option>
                    ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="ColorByPropControlSelect">
                <Form.Label>Color By</Form.Label>
                <Form.Control as="select" name="colorByPropId" 
                  defaultValue={this.state.colorByProp? this.state.colorByProp.id : undefined}>
                    {propertyIds.map((id: any, i: number) => (
                      <option key={i} value={id}>{this.state.propertiesById[id].name}</option>
                    ))}
                </Form.Control>
              </Form.Group>

              <Form.Group controlId="ColorscaleControlSelect">
                <Form.Label>Colorscale</Form.Label>
                <Form.Control as="select" name="colorscaleName" 
                  defaultValue={this.state.colorscaleName? this.state.colorscaleName : undefined}>
                    {plotlyColorscaleNames.map((name: string, i: number) => (
                      <option key={i} value={name}>{name}</option>
                    ))}
                </Form.Control>
              </Form.Group>

              <Button variant="primary" type="submit">
                Load
              </Button>
            </Form>
        );
    }

    closeModal() {
        this.setState({
            showModal: false,
        });
    }

    openModal() {
        this.setState({
            showModal: true,
        });
    }

    handleSubmit(evt: any) {
        evt.preventDefault();
        const data = new FormData(evt.target);
        const xPropId = parseInt(data.get('xPropId') as string);
        const yPropId = parseInt(data.get('yPropId') as string);
        const colorByPropId = parseInt(data.get('colorByPropId') as string);
        const chartType = data.get('chartType');
        const colorscaleName = data.get('colorscaleName');
        if (xPropId && yPropId && chartType && this.aoiBounds !== null && this.state.propertiesById) {
            this.refreshChartData(xPropId, yPropId, colorByPropId, chartType as string, colorscaleName as string);
        } else if (xPropId && yPropId && chartType) {
            this.setState({
                chartType,
                xProp: this.state.propertiesById[xPropId],
                yProp: this.state.propertiesById[yPropId],
                xPropId,
                yPropId,
                colorscaleName,
            });
        }
        this.socket.emit('chart', {
            command: 'loadChartData',
            xPropId,
            yPropId,
            colorByPropId,
            chartType,
            colorscaleName,
            widgetInstanceId: this.props.widgetInstance.id,
            widgetInstanceTitle: this.props.widgetInstance.title,
        });
        this.closeModal();
    }

    onPlotClick(data: any) {
        // console.log('onPlotClick', data);
        if (data.points && data.points.length > 0) {
            const point = data.points[0];
            const entityId = parseInt(point.hovertext);
            this.annotateSelectedEntity(entityId);
            this.socket.emit('chart', {
                command: 'selectEntity',
                entityId,
            });
        }
    }

    emitPlotDeselect() {
        // need some way to be able to reset property filter; this is
        // the best I can find so far. Only consistent way to trigger it is to
        // double click on an empty region of the plot.
        console.log('handlePlotDeselect');
        this.socket.emit('chart', {
            xStart: null,
            xEnd: null,
            yStart: null,
            yEnd: null,
            xPropName: null,
            yPropName: null,
            xPropId: null,
            yPropId: null,
            command: 'plotSelect',
        });
    }

    emitPlotSelect(params: any) {
        if (!params || !params.range || !params.range.x || !params.range.y) {
            // TODO this does give you lassoPoints, which could be converted
            // to a polygon and each point could be tested for whether it
            // is contained (more efficient than sending entire set of selected entity ids)
            console.log('non-rectangular select not yet supported', params);
            return;
        }

        if (!this.state.propertiesById) {
            console.log('property list not loaded yet');
            return;
        }
        // console.log('chartWidget handlePlotSelect', params);

        const [xStart, xEnd] = params.range.x;
        const [yStart, yEnd] = params.range.y;

        this.socket.emit('chart', {
            xStart,
            xEnd,
            yStart,
            yEnd,
            xPropName: this.state.propertiesById[this.state.xPropId].name,
            yPropName: this.state.propertiesById[this.state.yPropId].name,
            xPropId: this.state.xPropId,
            yPropId: this.state.yPropId,
            command: 'plotSelect',
        });
    }

    render() {
        // console.log('chartWidget', this.props.widgetInstance);
        // console.log('render plot revision', this.state.revision, JSON.stringify(this.state.annotations));
        return (
            <div className="chart-container" style={{ width: '100%', height: '100%' }}>
                <Button variant="primary" size="sm" style={{ position: 'absolute', top: 0, left: 0 }} 
                    onClick={() => this.openModal()}>+</Button>

                {this.state.isLinkedToMap ?
                   <FontAwesomeIcon icon={faLink} style={{ position: 'absolute', top: 7, left: 34 }} />
                    : null 
                }

                <Plot
                config={{
                    modeBarButtonsToRemove: [
                        // names defined here https://github.com/plotly/plotly.js/blob/master/src/components/modebar/buttons.js
                        'lasso2d',
                        'autoScale2d',
                        'hoverCompareCartesian',
                        'toggleSpikelines',
                    ]
                }}
                data={[
                    {
                        x: this.state.xData,
                        y: this.state.yData,
                        hovertext: this.state.hoverText,
                        type: this.state.chartType ? this.state.chartType : 'scattergl',
                        mode: 'markers',
                        // marker: {color: '#28A745'},
                        marker: {
                            color: this.state.colorByData,
                            opacity: this.state.opacityData,
                            colorscale: this.state.colorscaleName ? this.state.colorscaleName : 'Viridis',
                            colorbar: {
                                title: this.state.colorByProp ? this.state.colorByProp.name : null,
                                side: 'right',
                            },
                            cmin: this.state.colorByMin !== null ? this.state.colorByMin : undefined,
                            cmax: this.state.colorByMax !== null ? this.state.colorByMax : undefined,
                        },
                    },
                ]}
                
                onSelected={(params: any) => this.emitPlotSelect(params)}
                onDeselect={() => this.emitPlotDeselect()}
                useResizeHandler
                style={{ width: '100%', height: '100%' }}
                // https://github.com/plotly/react-plotly.js/issues/76
                layout={{
                    dragmode: 'select', // makes box select the default modebar tool
                    hovermode: 'closest', // more obvious behavior (select point closest to where you hover/click) than default (select some point on axis)
                    plot_bgcolor: '#2F3035',
                    paper_bgcolor: '#2F3035',
                    font: {
                        color: 'white',
                        family: "'Roboto', sans-serif",
                    },
                    annotations: this.state.annotations,
                    datarevision: this.state.revision, // make sure new annotation renders
                    autosize: true,
                    margin: {
                        t: 50,
                        b: 80,
                    },
                    title: {
                        // text: this.state.loadingChartData ? '(loading)' : '',
                    },
                    xaxis: {
                        title: this.state.xProp ? this.state.xProp.name : null, 
                        color: 'white', // grid lines are this color blended with background if you don't specify gridcolor
                        // other relevant options if defaults aren't want you want:
                        // tickcolor: 'white',
                        // linecolor: 'white',
                        // zerolinecolor: 'white',
                        // gridcolor: 'white',
                    },
                    yaxis: {
                        title: this.state.yProp ? this.state.yProp.name : null,
                        color: 'white', // grid lines are this color blended with background if you don't specify gridcolor
                    },
                }}
                onClick={(data: any) => this.onPlotClick(data)}
                revision={this.state.revision}
                />

                <Modal show={this.state.showModal} onHide={() => this.closeModal()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Configure chart</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>

                      {this.chartPropertiesForm()}

                    </Modal.Body>
                </Modal>

            </div>
        );
    }
}

export default withApollo<DashboardChartWidgetProps>(DashboardChartWidget);
